import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCatalogs, catalogMap, invalidateContacts } from "@/lib/lookups";
import { CONTACT_FIELDS } from "@/lib/conecta";
import { fmtDate } from "@/lib/finance";
import { FieldsForm } from "@/components/finance/FieldsForm";
import { Timeline } from "@/components/finance/Timeline";
import { DetailFields, Section } from "@/components/common/DetailFields";
import { BackLink, PrivateBadge, SemDot, ShareButtons } from "@/components/conecta/Bits";
import { ContactOnePages } from "@/components/conecta/OnePagesSection";
import { ContactRedSection } from "@/components/red/ContactRedSection";

const ROLE = { quien_pide: "Pide", quien_resuelve: "Resuelve", participante: "Participa" };

const OppRow = ({ o, stages }) => (
  <Link to={`/conecta/negocios/${o.id}`} data-testid="contact-opportunity-item" className="flex items-start gap-3 py-3">
    <SemDot value={o.semaphore} size="w-3 h-3 mt-1.5" />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm truncate">{o.title}</p>
      <p className="text-xs c-text-2">{stages[o.stage] || o.stage} · {ROLE[o.role]}</p>
      <p className="text-xs c-muted">Próximo: {o.next_step_text || "Sin definir"}{o.next_step_date ? ` · ${fmtDate(o.next_step_date)}` : ""}</p>
      <p className="text-[11px] c-muted">Último contacto: {fmtDate(o.last_contact_date) || "Sin definir"}</p>
    </div>
  </Link>
);

export default function ContactDetailPage() {
  const { id } = useParams();
  const cats = useCatalogs();
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const labels = useMemo(() => Object.fromEntries(CONTACT_FIELDS.map((f) => [f.key, f.label])), []);
  const load = useCallback(() => api.get(`/contacts/${id}`).then((r) => setC(r.data)).catch((e) => { setC(false); toast.error(errMsg(e)); }), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    if (!Object.keys(payload).length) return setEditing(false);
    try {
      setC((await api.patch(`/contacts/${id}`, payload)).data);
      invalidateContacts();
      setEditing(false);
      toast.success("Contacto actualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (c === false) return <div className="space-y-3"><BackLink to="/conecta/red" label="Red" /><p data-testid="contact-not-found" className="c-card p-6 text-center c-text-2">Contacto no encontrado.</p></div>;
  if (!c) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  const stages = catalogMap(cats, "stages");
  return (
    <div data-testid="contact-detail-page" className="space-y-4">
      <BackLink to="/conecta/red" label="Red" />
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 data-testid="contact-detail-name" className="font-heading text-2xl font-extrabold tracking-tight">{c.name}</h2>
          <PrivateBadge show={c.is_private_only_me} />
        </div>
        <p className="c-text-2 text-sm">{[c.nickname && `"${c.nickname}"`, c.company, c.position, catalogMap(cats, "relationship_types")[c.relationship_type]].filter(Boolean).join(" · ")}</p>
        {c.contact_kind === "empresa" && <span data-testid="contact-company-badge" className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border c-line c-muted">Empresa</span>}
        {c.company_contact_id && c.names[c.company_contact_id] && (
          <Link to={`/conecta/red/${c.company_contact_id}`} data-testid="contact-company-link" className="block text-sm c-accent font-semibold hover:underline">Empresa: {c.names[c.company_contact_id]}</Link>
        )}
      </div>
      {c.members?.length > 0 && (
        <Section title={`Personas de ${c.name} (${c.members.length})`} testid="contact-members">
          <ul className="space-y-1.5">{c.members.map((m) => (
            <li key={m.id}><Link to={`/conecta/red/${m.id}`} data-testid="contact-member-link" className="text-sm hover:underline">{m.name}{m.position ? <span className="c-text-2"> · {m.position}</span> : null}</Link></li>
          ))}</ul>
        </Section>
      )}
      <ShareButtons phone={c.phone} whatsapp={c.whatsapp} email={c.email} text={`Hola ${c.nickname || c.name.split(" ")[0]}, `} />
      <Section title={`¿Qué tengo con ${c.contact_kind === "empresa" ? c.name : c.nickname || c.name.split(" ")[0]}? (${c.opportunities.length})`} testid="contact-opportunities">
        <div className="divide-y c-line">
          {c.opportunities.length === 0 && <p className="py-3 text-sm c-muted">Sin negocios todavía.</p>}
          {c.opportunities.map((o) => <OppRow key={o.id} o={o} stages={stages} />)}
        </div>
      </Section>
      <DetailFields fields={CONTACT_FIELDS} item={c} onEdit={() => setEditing(true)} />
      <ContactRedSection c={c} onUpdate={setC} />
      {c.introduced_contacts.length > 0 && (
        <Section title="Presentó a" testid="contact-introduced">
          <div className="flex flex-wrap gap-2">{c.introduced_contacts.map((x) => <Link key={x.id} to={`/conecta/red/${x.id}`} className="text-sm px-3 py-1 rounded-full bg-[var(--c-elev)]">{x.name}</Link>)}</div>
        </Section>
      )}
      <ContactOnePages contactId={c.id} />
      <Section title="Historial inmutable" testid="detail-timeline"><Timeline events={c.timeline} labels={labels} /></Section>
      <FieldsForm open={editing} onOpenChange={setEditing} title="Editar contacto" fields={CONTACT_FIELDS} item={c} onSubmit={save} testid="edit-contact-sheet" />
    </div>
  );
}
